import { createContext, useContext } from 'react'
import type { HostingMode, Provider, SiteProfile, UiSettings } from './types'

export const ALL_PROVIDERS: Provider[] = ['alibaba', 'anthropic', 'aws', 'azure', 'cloudflare', 'core42', 'crusoe', 'databricks', 'deepinfra', 'fireworks', 'deepinfra', 'gemini', 'gcp', 'groq', 'hfendpoints', 'huawei', 'watsonx', 'mistral', 'nebius', 'oci', 'openai', 'snowflake', 'telnyx', 'together', 'xai']
export const VENDOR_LABEL: Record<Provider, string> = { aws: 'AWS', azure: 'Azure', oci: 'OCI', core42: 'Core42', alibaba: 'Alibaba', gcp: 'Google', nebius: 'Nebius', mistral: 'Mistral', telnyx: 'Telnyx', snowflake: 'Snowflake', cloudflare: 'Cloudflare', databricks: 'Databricks', gemini: 'Gemini API', hfendpoints: 'HF Endpoints', watsonx: 'IBM watsonx', xai: 'xAI', together: 'Together AI', crusoe: 'Crusoe', huawei: 'Huawei Cloud', groq: 'Groq', fireworks: 'Fireworks AI', deepinfra: 'DeepInfra', openai: 'OpenAI', anthropic: 'Anthropic' }

/** Used until the server answers, and if it cannot: everything on. */
/** The hosting modes each vendor offers at all (the administrator can hide some). */
export const OFFERS_ALL: Record<Provider, HostingMode[]> = {
  aws: ['on_demand', 'self_hosted'], azure: ['on_demand', 'self_hosted'],
  oci: ['on_demand', 'self_hosted', 'dedicated'], core42: ['on_demand', 'self_hosted'], alibaba: ['on_demand', 'self_hosted'], gcp: ['on_demand', 'self_hosted'],
  nebius: ['on_demand', 'self_hosted'],
  mistral: ['on_demand'],
  telnyx: ['on_demand'],
  snowflake: ['on_demand'],
  cloudflare: ['on_demand'],
  databricks: ['on_demand'],
  gemini: ['on_demand'],
  hfendpoints: ['self_hosted'],
  watsonx: ['on_demand', 'self_hosted'],
  xai: ['on_demand'],
  together: ['on_demand', 'self_hosted'],
  crusoe: ['on_demand', 'self_hosted'],
  huawei: ['on_demand'],
  groq: ['on_demand'],
  fireworks: ['on_demand', 'self_hosted'],
  deepinfra: ['on_demand', 'self_hosted'],
  openai: ['on_demand'], anthropic: ['on_demand'],
}

/** The site, as the page takes it until the server has answered (pricing/site.py says the same). */
export const SITE: SiteProfile = {
  id: 'opex', name: 'LLM Opex', tagline: '', contact_email: 'info@llmopex.com', layout: 'wide',
  features: { landing: true, footer: true, newsletter: true, legal: true, question_titles: true, locate: true, explore: true }, compare_default: [],
}

export const DEFAULT_SETTINGS: UiSettings = {
  vendors: [...ALL_PROVIDERS],
  modes: { ...OFFERS_ALL },
  suggestion_rule: 'lowest',
  calculator_defaults: {
    model: 'openai/gpt-oss-120b', precision: '', output_tokens_per_day: 2_000_000,
    input_tokens_per_day: 6_000_000, tokens_per_second: 2500, utilization: 0.5,
    options: ['oci:on_demand', 'oci:dedicated', 'aws:on_demand'],
  },
  notice: '',
  cost_over_time: false,
  reserved_capacity: true,
  assessment: { enabled: false, free: false, price_usd: null, currency: 'usd' },
  trending: { what_it_means: true, retirements: true, announcements: true },
  newsletter: false,
  site: { operator: '', contact_email: '', jurisdiction: '' },
  profile: SITE,
}

export const SettingsContext = createContext<UiSettings>(DEFAULT_SETTINGS)

/** The administrator's settings: vendors that are on, calculator defaults, notice. */
export const useSettings = () => useContext(SettingsContext)
/** The site this server is; the name and what is on. */
export const useSite = (): SiteProfile => useContext(SettingsContext).profile ?? SITE

/** The modes a vendor offers that the administrator shows. */
export const modesOn = (settings: UiSettings, p: Provider): HostingMode[] =>
  (settings.modes?.[p] ?? OFFERS_ALL[p]).filter((m) => OFFERS_ALL[p].includes(m))

/** `order`, keeping only the vendors that are on. */
export const enabledIn = (order: Provider[], vendors: Provider[]) => order.filter((p) => vendors.includes(p))
